import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { sanitizeString } from '@e3dc-react/shell/Libs/strings'

const useStyles = makeStyles<{ textColor?: string }>()((theme, props) => ({
  labelWrapper: {
    '& > *': {
      fill: props.textColor ?? theme.palette.text.primary,
    },
  },
}))

const PADDING_Y_ACTION = 18
const OFFSET_ACTION_X = 20
const CHARACTER_WIDTH = 7
const CHARACTER_WIDTH_UPSM = 7
const MAX_CHARS_WITH_ICON = 20

/*
 * This component renders a custom pie chart label displayed in the middle of the chart
 */
const CustomLabel: React.FC<{
  name?: string | React.ReactElement
  value: string
  offset: number
  innerWidth: number
  cx: number
  cy: number
  additionalUnit?: string
  showAsterix?: boolean
  icon?: React.ReactElement
  iconButton?: React.ReactElement
  textColor?: string
}> = ({ name, value, innerWidth, offset, cx, cy, additionalUnit, showAsterix, iconButton, icon, textColor }) => {
  const { classes } = useStyles({ textColor })
  const theme = useTheme()
  const isStringName = typeof name === 'string'
  const upSm = useMediaQuery(theme.breakpoints.up('sm'))
  const characterWidth = upSm ? CHARACTER_WIDTH_UPSM : CHARACTER_WIDTH
  const maxChars = icon ? MAX_CHARS_WITH_ICON : Math.floor(innerWidth / characterWidth)
  const additionalOffset = icon && !name ? 10 : icon && name ? 20 : 5

  const renderStringName = (name: string): React.ReactNode => {
    const sanitizedName = sanitizeString(name)
    const truncatedName = sanitizedName.length > maxChars ? `${sanitizedName.slice(0, maxChars)}...` : sanitizedName

    return (
      <Typography sx={{ marginTop: 6 }} component="tspan" x={cx} dy={showAsterix ? '2em' : '1.5em'} variant="caption" alignSelf={'center'}>
        {truncatedName}
      </Typography>
    )
  }

  const renderIconName = (name: React.ReactElement): React.ReactNode => {
    const dxWithAterix = upSm ? 42 : 36
    const dx = upSm ? 40 : 36
    return <g transform={`translate(${showAsterix ? dxWithAterix : dx}, ${cy + 6})`}>{name}</g>
  }

  return (
    <>
      <Stack
        component={'text'}
        className={`${classes.labelWrapper} recharts-text recharts-CustomLabel`}
        offset={offset}
        x={cx - 5}
        y={cy - additionalOffset}
        textAnchor="middle"
      >
        <Typography component="tspan" x={cx} dy="0.355em" variant="h1" alignSelf={'center'}>
          {value}
          {additionalUnit && (
            <Typography component="tspan" variant="h2" color="text.secondary">
              {` ${additionalUnit}`}
            </Typography>
          )}
          {showAsterix && (
            <Typography dy={'-0.5em'} variant="h3" component="tspan" color="text.secondary">
              {'*'}
            </Typography>
          )}
        </Typography>
        {isStringName && renderStringName(name)}
      </Stack>
      {icon && renderIconName(icon)}
      {iconButton && (
        <foreignObject x={cx - OFFSET_ACTION_X} y={cy + PADDING_Y_ACTION} width={27} height={27}>
          {iconButton}
        </foreignObject>
      )}
    </>
  )
}

export default CustomLabel
